import React, { type ReactNode, useMemo } from 'react';
import styled from 'styled-components';
import { useElementSize } from '../../hooks';
import { Button, IconButton } from '../Button';
import { Box, type BoxProps, HStack, Stack, VStack } from '../Core';
import { Checkbox, FormControlSizes } from '../Form';
import { Icon, IconName } from '../Icons';
import { type SortState, SortableBox } from '../SortableBox/SortableBox';
import { Text } from '../Text';
import { Tooltip } from '../Tooltip';

export type SelectionListSortProperty = 'label' | 'suffix';

export const SELECTION_LIST_DEFAULT_ITEM_HEIGHT = '18px';
export const SELECTION_LIST_TALLER_ITEM_HEIGHT = '36px'; // twice the default height

export interface SelectionListItem {
  selected: boolean;
  disabled: boolean;
  key: string;
  label: string;
  /** If you want to display a parent-child hierarchy when showAll is set to true, provide a parentKey for each item */
  parentKey?: string;

  /** A warning node rendered in a tooltip if provided */
  warning?: ReactNode;
  /** A suffix rendered to the far-right of the item in the row */
  suffix?: ReactNode;
}

interface GroupItem {
  parentKey: string;
  parentItem: SelectionListItem;
  items: SelectionListItem[];
}

interface SelectionsListProps {
  items: SelectionListItem[];
  /** Placeholder text shown when there are no selected items */
  placeholder?: string;
  /** If true, will show all items, not only the selected items. This will also give each item a checkbox. It will render parents (if provided). */
  showAll?: boolean;
  /** Called when the show all toggle button is clicked */
  onShowAllChange?: (value: boolean) => void;
  onSelect?: (key: string) => void;
  onDelete?: (key: string) => void;
  onDeleteAll?: () => void;
  onSelectAll?: () => void;
  onOnlyClicked?: (key: string) => void;

  // Parent properties. Provide these if you are intending to render parent rows (via "showAll"=true)
  /** Called when the parent checkbox is selected. The parent select state is derived from its children's selected state. */
  onSelectParent?: (parentKey: string, selected: boolean) => void;
  getParentLabel?: (parentKey: string) => string;
  /** A callback for sorting parent SelectionListItems (derived). The key will be the parentKey taken from the relevant child item. */
  parentSortComparator?: (a: SelectionListItem, b: SelectionListItem) => number;

  /** Height of the entire list */
  h?: BoxProps['h'];
  /** Render an arbitrary react node as a suffix (far-right) element in the list's header */
  getHeaderSuffix?: () => ReactNode;
  /** Optional. Override the individual item height */
  itemHeight?: string;
  /** The sorting state, if any, to apply to the list of selections to render */
  sort?: SortState<SelectionListSortProperty>;
  /** A callback for sorting child SelectionListItems */
  sortComparator?: (a: SelectionListItem, b: SelectionListItem) => number;
  /** An onChange handler  */
  onSortChange?: (newSort: SortState<SelectionListSortProperty> | undefined) => void;
}

export const SelectionsList = ({
  items,
  placeholder = 'No selected markets',
  showAll,
  onShowAllChange,
  onSelect,
  onSelectParent,
  getParentLabel,
  onDelete,
  onDeleteAll,
  onSelectAll,
  h,
  getHeaderSuffix,
  itemHeight = SELECTION_LIST_DEFAULT_ITEM_HEIGHT,
  sort,
  sortComparator,
  parentSortComparator,
  onSortChange,
  onOnlyClicked,
}: SelectionsListProps) => {
  const headerSuffix = useMemo(() => getHeaderSuffix?.(), [getHeaderSuffix]);

  const { elementRef, size } = useElementSize<HTMLDivElement>();
  const isScrolled = useMemo(
    () => size.scrollHeight && size.clientHeight && size.scrollHeight > size.clientHeight,
    [size.scrollHeight, size.clientHeight]
  );

  const selectedItemCount = useMemo(
    () => items.reduce((count, item) => (item.selected ? count + 1 : count), 0),
    [items]
  );

  /** When showAll is true, we show all the provided items. Otherwise, we just show selected items in the list. */
  const shownItems = useMemo(() => (showAll ? items : items.filter(item => item.selected)), [items, showAll]);

  /* Below we separate into two flows: flatItems and groupedItems. Flat for showAll = false, grouped for showAll = true. */
  const flatItems = useMemo(
    () => (showAll ? undefined : shownItems.toSorted(sortComparator)),
    [showAll, shownItems, sortComparator]
  );

  const groupedItems = useMemo(() => {
    if (!showAll) {
      return undefined;
    }
    const groups = new Map<string, SelectionListItem[]>();
    for (const item of shownItems) {
      // If the item isn't providing a parent, it is its "own parent" (technically), which will make it render "collapsed"
      if (!item.parentKey) {
        // we dont have a parent, just put the item in its own group
        groups.set(item.key, [item]);
      }
      if (item.parentKey) {
        const group = groups.get(item.parentKey) ?? [];
        groups.set(item.parentKey, [...group, item]);
      }
    }

    // We now have a map of all our items in their groups. Lets sort all the internal arrays
    let parentItems: GroupItem[] = [];
    for (const [parentKey, items] of groups) {
      parentItems.push({
        parentKey,
        parentItem: getParentItem(parentKey, getParentLabel?.(parentKey) ?? parentKey, items),
        items: items.toSorted(sortComparator),
      });
    }

    // And then we also sort the parent items themselves
    if (parentSortComparator) {
      parentItems = parentItems.toSorted((a, b) => parentSortComparator?.(a.parentItem, b.parentItem));
    }

    return parentItems;
  }, [shownItems, getParentLabel, sortComparator, parentSortComparator, showAll]);

  const headerCheckboxState = useMemo(() => {
    if (!groupedItems) {
      // the header checkbox is only rendered if there are groupedItems defined
      return undefined;
    }

    const allSelected = !groupedItems.some(group => !group.parentItem.selected);
    if (allSelected) {
      return 'checked';
    }
    const someSelection = groupedItems.some(group => group.parentItem.selected);
    if (someSelection) {
      return 'indeterminate';
    }

    return 'unchecked';
  }, [groupedItems]);

  if (shownItems.length === 0) {
    return (
      <VStack
        w="100%"
        h={h}
        p="spacing12"
        borderRadius="borderRadiusMedium"
        borderWidth="2px"
        borderStyle="solid"
        borderColor="backgroundCard"
        data-testid="selection-list-no-selections"
        gap="spacing8"
      >
        <Text fontSize="fontSizeSm">{placeholder}</Text>
      </VStack>
    );
  }

  return (
    <Stack fontSize="fontSizeSmall" gap="spacing4" data-testid="selections-list" overflow="hidden">
      <HStack
        justifyContent="space-between"
        data-testid="selections-list-header"
        pr={isScrolled ? 'scrollbarSize' : undefined}
      >
        <HStack gap="spacing4">
          {headerCheckboxState && onDeleteAll && onSelectAll && (
            <Checkbox
              size={FormControlSizes.Small}
              checked={headerCheckboxState === 'checked'}
              indeterminate={headerCheckboxState === 'indeterminate'}
              onChange={() => (headerCheckboxState === 'checked' ? onDeleteAll() : onSelectAll())}
              disabled={groupedItems?.length === 0}
              data-testid="selection-list-header-checkbox"
            />
          )}

          <SortableBox
            sort={sort}
            sortKey="label"
            onChange={onSortChange}
            gap="spacing2"
            data-testid="selections-list-header-sortable"
          >
            <Text>Markets ({selectedItemCount})</Text>
          </SortableBox>
          {onShowAllChange && (
            <Tooltip tooltip={showAll ? 'Show Selected' : 'Show All'}>
              <IconButton
                onClick={() => onShowAllChange(!showAll)}
                size={FormControlSizes.Xxs}
                icon={showAll ? IconName.ListCollapse : IconName.ListExpand}
                data-testid="selection-list-show-all-button"
                dim
              />
            </Tooltip>
          )}
        </HStack>

        <HStack gap="spacing2">
          <SortableBox
            sort={sort}
            sortKey="suffix"
            onChange={onSortChange}
            arrowPlacement="left"
            gap="spacing2"
            data-testid="suffix-header-sortable"
          >
            {headerSuffix}
          </SortableBox>

          {onDeleteAll && !showAll && (
            <IconButton
              ghost
              size={FormControlSizes.Tiny}
              height={SELECTION_LIST_DEFAULT_ITEM_HEIGHT}
              width={SELECTION_LIST_DEFAULT_ITEM_HEIGHT}
              icon={IconName.Close}
              onClick={() => onDeleteAll()}
              data-testid="selections-list-delete-all"
              color="colorTextSubtle"
            />
          )}
        </HStack>
      </HStack>

      <Stack gap="spacing4" maxHeight={h} pb="spacing2" overflow="auto" overflowX="hidden" ref={elementRef}>
        {/* Conditional rendering based on flatItems or groupedItems being present (based on showAll state). Split up entirely just for simplicity. */}
        {flatItems ? (
          flatItems.map(item => (
            <SelectionItem
              key={item.key}
              item={item}
              onOnlyClicked={shownItems.length > 1 ? onOnlyClicked : undefined}
              itemHeight={itemHeight}
              onSelect={onSelect}
              onDelete={onDelete}
              isParent={false}
            />
          ))
        ) : groupedItems ? (
          groupedItems.map(({ parentItem, items }) => {
            const renderParent = items.length > 1;
            return (
              <React.Fragment key={parentItem.key}>
                {renderParent && (
                  <SelectionItem
                    key={parentItem.key}
                    item={parentItem}
                    indented={false}
                    showCheckbox={showAll}
                    itemHeight={SELECTION_LIST_DEFAULT_ITEM_HEIGHT} // the parent always gets this default (tighter) height. Parents dont have suffixes, so dont need extra height.
                    onSelect={() => onSelectParent?.(parentItem.key, true)}
                    onDelete={() => onSelectParent?.(parentItem.key, false)}
                    isParent={true}
                  />
                )}
                {items.map(item => (
                  <SelectionItem
                    key={item.key}
                    item={item}
                    indented={renderParent}
                    onOnlyClicked={shownItems.length > 1 ? onOnlyClicked : undefined}
                    itemHeight={itemHeight}
                    showCheckbox={showAll}
                    onSelect={onSelect}
                    onDelete={onDelete}
                    isParent={false}
                  />
                ))}
              </React.Fragment>
            );
          })
        ) : (
          <></> /* shouldnt happen */
        )}
      </Stack>
    </Stack>
  );
};

function getParentItem(parentKey: string, parentLabel: string, children: SelectionListItem[]): SelectionListItem {
  const selected = children.some(child => child.selected);
  const someChildSelectable = children.some(child => !child.disabled);
  const warning = someChildSelectable ? undefined : 'No Accounts selectable within Market';

  return {
    key: parentKey,
    selected, // a parent being selected means indeterminate
    disabled: selected ? false : !someChildSelectable,
    label: parentLabel,
    warning,
  };
}

interface SelectionItemProps extends Pick<
  SelectionsListProps,
  'onDelete' | 'onOnlyClicked' | 'onSelect' | 'itemHeight'
> {
  item: SelectionListItem;
  showCheckbox?: boolean;
  indented?: boolean;
  isParent: boolean;
}

const SelectionItem = ({
  item,
  onDelete,
  onOnlyClicked,
  onSelect,
  showCheckbox,
  itemHeight,
  indented,
  isParent,
}: SelectionItemProps) => {
  const { warning, label, suffix, key, disabled, selected } = item;
  const textColor = disabled && !selected ? 'colorTextMuted' : 'colorTextImportant';

  return (
    <Row
      w="100%"
      minHeight={itemHeight}
      data-testid="selection-list-item"
      data-itemkey={key}
      data-parent={isParent}
      data-selected={selected}
    >
      <HStack w="100%" h="100%" justifyContent="space-between">
        {/* Left bit */}
        <HStack gap="spacing4" h="100%" color={textColor}>
          {indented && <Icon icon={IconName.Connector} color="colorTextMuted" />}
          {showCheckbox ? (
            <Checkbox
              size={FormControlSizes.Small}
              checked={selected}
              onChange={e => (selected ? onDelete?.(item.key) : onSelect?.(item.key))}
              disabled={disabled}
              data-testid="selection-list-item-checkbox"
            >
              {label}
            </Checkbox>
          ) : (
            <>
              {/* Pointer-events none because the text cursor is ugly and feels bad */}
              <Text style={{ cursor: 'default' }}>{label}</Text>
            </>
          )}

          {warning && (
            <Tooltip tooltipTestID="selection-item-warning-tooltip-content" tooltip={warning}>
              <Icon data-testid="selection-item-warning-icon" icon={IconName.DotSolid} color="yellow.lighten" />
            </Tooltip>
          )}

          {onOnlyClicked && (
            <OnlyButton
              startIcon={IconName.EyeShow}
              dim
              size={FormControlSizes.Xxs}
              disabled={disabled}
              onClick={() => onOnlyClicked(key)}
              data-testid="selection-list-item-only"
            >
              Only
            </OnlyButton>
          )}
        </HStack>

        {/* Right bit */}
        <HStack data-testid="selection-list-item-suffix">
          {suffix}
          {!showCheckbox && onDelete && (
            <IconButton
              ghost
              height={SELECTION_LIST_DEFAULT_ITEM_HEIGHT}
              width={SELECTION_LIST_DEFAULT_ITEM_HEIGHT}
              size={FormControlSizes.Tiny}
              icon={IconName.Close}
              onClick={() => onDelete(key)}
              data-testid="selection-list-item-delete"
              color="colorTextSubtle"
            />
          )}
        </HStack>
      </HStack>
    </Row>
  );
};

// These styles make it so the OnlyButton only shows when the row its in is hovered
const OnlyButton = styled(Button)``;
const Row = styled(Box)`
  ${OnlyButton} {
    display: none;
  }

  &:hover {
    ${OnlyButton} {
      display: inline-flex;
    }
  }
`;
